import { computed, inject } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { delay, of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, switchMap, tap } from 'rxjs/operators';
import { ConfigService, CustomerService, MailConfigService, PentestCalendarService, PentestService } from '../services';
import { CreatePentestConfigData, MailConfig, UpdateDataSharingData } from '../models';
import { PentestScanReport } from '../enums';
import {
  initialModuleConfigurationsState,
  ModuleConfigurationsState,
} from '../models/state/module-configurations.state';
import { SelectedCustomerStore } from './selected-customer.store';

export const ModuleConfigurationsStore = signalStore(
  withProps(() => {
    let customerSignal = inject(SelectedCustomerStore)?.selectedCustomer;
    return { customer: computed(() => customerSignal()) };
  }),
  withState<ModuleConfigurationsState>(initialModuleConfigurationsState),
  withMethods(
    (
      store,
      pentestService = inject(PentestService),
      pentestCalendarService = inject(PentestCalendarService),
      configService = inject(ConfigService),
      mailConfigService = inject(MailConfigService),
      customerService = inject(CustomerService),
      toastr = inject(ToastrService),
      translate = inject(TranslateService),
    ) => {
      const loadCalendarEvents = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { calendarEventsLoading: true })),
          switchMap(() =>
            pentestCalendarService.getCustomerPentestEvents(store.customer()?.id).pipe(
              tap((calendarEvents) => patchState(store, { calendarEvents })),
              catchError(() => {
                patchState(store, { calendarEventsLoading: false });
                return of();
              }),
            ),
          ),
        ),
      );
      const loadModuleConfigurations = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { scanConfigsLoading: true })),
          switchMap(() =>
            configService.getScanConfigurations(store.customer()?.id).pipe(
              tap((scanConfigs) => patchState(store, { scanConfigs, scanConfigsLoading: false })),
              catchError(() => {
                patchState(store, { scanConfigsLoading: false });
                return of();
              }),
            ),
          ),
        ),
      );
      const getModuleConfiguration = rxMethod<string>(
        pipe(
          switchMap((configId) =>
            configService.getScanConfig(store.customer()?.id, configId).pipe(
              catchError(() => {
                return of();
              }),
            ),
          ),
        ),
      );
      const deleteModuleConfiguration = rxMethod<string>(
        pipe(
          tap(() => patchState(store, { scanConfigsLoading: true })),
          switchMap((configId) =>
            configService.deleteScanConfig(store.customer()?.id, configId).pipe(
              tap(() => toastr.success(translate.instant('moduleConfigurations.scanConfigs.alerts.deleteSuccess'))),
              tap(() => loadModuleConfigurations()),
              catchError(() => {
                patchState(store, { scanConfigsLoading: false });
                toastr.error(translate.instant('moduleConfigurations.scanConfigs.alerts.deleteFailure'));
                return of();
              }),
            ),
          ),
        ),
      );
      const performModuleConfigurationAction = rxMethod<{ configId: string; action: string }>(
        pipe(
          tap(() => patchState(store, { scanConfigsLoading: true })),
          switchMap(({ configId, action }) =>
            configService.performScanConfigAction(store.customer()?.id, configId, action).pipe(
              tap(() => toastr.success(translate.instant('moduleConfigurations.scanConfigs.alerts.actionSuccess'))),
              delay(40_000), // no synchronous actions for e.g. 'start'. Backend timer is 30 seconds, prevent double click by waiting
              tap(() => loadModuleConfigurations()),
              catchError(() => {
                patchState(store, { scanConfigsLoading: false });
                toastr.error(translate.instant('moduleConfigurations.scanConfigs.alerts.actionFailure'));
                return of();
              }),
            ),
          ),
        ),
      );
      const updateModuleConfiguration = rxMethod<{
        configId: string;
        data: CreatePentestConfigData;
      }>(
        pipe(
          tap(() => patchState(store, { scanConfigsLoading: true })),
          switchMap(({ configId, data }) =>
            configService.updateScanConfig(store.customer()?.id, configId, data).pipe(
              tap(() => toastr.success(translate.instant('moduleConfigurations.scanConfigs.alerts.updateSuccess'))),
              tap(() => loadModuleConfigurations()),
              catchError(() => {
                patchState(store, { scanConfigsLoading: false });
                toastr.error(translate.instant('moduleConfigurations.scanConfigs.alerts.updateFailure'));
                return of();
              }),
            ),
          ),
        ),
      );
      const downloadPentestReport = rxMethod<{
        pentestId: string;
        report: PentestScanReport;
        encryptReport: boolean;
        historyId?: string;
      }>(
        pipe(
          tap(({ pentestId, historyId, report }) =>
            patchState(store, {
              pentestReportsLoading: [...store.pentestReportsLoading(), `${historyId ?? pentestId}-${report}`],
            }),
          ),
          concatMap(({ pentestId, report, historyId, encryptReport }) =>
            pentestService
              .downloadPentestReport(pentestId, report, encryptReport, historyId, store.customer()?.id)
              .pipe(
                tap(() => toastr.success(translate.instant('dashboard.alerts.downloadReportSuccess'))),
                catchError(() => {
                  toastr.error(translate.instant('dashboard.alerts.downloadReportFail'));
                  return of();
                }),
                finalize(() =>
                  patchState(store, {
                    pentestReportsLoading: [
                      ...store
                        .pentestReportsLoading()
                        .filter((pentestReport) => pentestReport !== `${historyId ?? pentestId}-${report}`),
                    ],
                  }),
                ),
              ),
          ),
        ),
      );

      const loadTemplates = rxMethod<void>(
        switchMap(() =>
          pentestService.getTemplates(store.customer()?.id).pipe(
            tap((templates) => patchState(store, { templates })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      const loadRunners = rxMethod<void>(
        switchMap(() =>
          pentestService.getRunners(store.customer()?.id).pipe(
            tap((runners) => patchState(store, { runners })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      const loadAssets = rxMethod<void>(
        switchMap(() =>
          pentestService.getAssets(store.customer()?.id).pipe(
            tap((assets) => patchState(store, { assets })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      const loadNessusConfigs = rxMethod<void>(
        switchMap((customerId) =>
          pentestService.getNessusConfigs(store.customer()?.id).pipe(
            tap((nessusConfigs) => patchState(store, { nessusConfigs })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      const createScan = rxMethod<CreatePentestConfigData>(
        pipe(
          switchMap((data) =>
            configService.createScanConfig(store.customer()?.id, data).pipe(
              tap(() => toastr.success(translate.instant('dashboard.alerts.scanCreateSuccess'))),
              tap(() => loadModuleConfigurations()),
              catchError(() => {
                toastr.error(translate.instant('dashboard.alerts.scanCreateFail'));
                return of(); // Prevent rxMethod from crashing
              }),
            ),
          ),
        ),
      );

      const loadDataSharingStatus = rxMethod<void>(
        pipe(
          switchMap(() =>
            customerService.getDataSharingStatus().pipe(
              tap((initialized) => patchState(store, { isDataSharingInitialized: initialized })),
              catchError(() => of()), // Prevent rxMethod from crashing
            ),
          ),
        ),
      );

      const updateDataSharingData = rxMethod<UpdateDataSharingData>(
        pipe(switchMap((data) => customerService.updateSharingData(data))),
      );

      const updatePentestMailSettings = rxMethod<{ configId: string; config: MailConfig }>(
        pipe(
          tap(() => patchState(store, { scanConfigsLoading: true })),
          switchMap(({ configId, config }) =>
            mailConfigService.updatePentestMailConfig(configId, config, store.customer()?.id).pipe(
              tap(() => toastr.success(translate.instant('moduleConfigurations.scanConfigs.alerts.updateSuccess'))),
              tap(() => loadModuleConfigurations()),
              catchError(() => {
                tap(() => patchState(store, { scanConfigsLoading: false }));
                toastr.error(translate.instant('moduleConfigurations.scanConfigs.alerts.updateFailure'));
                return of();
              }),
            ),
          ),
        ),
      );

      return {
        loadCalendarEvents,
        loadModuleConfigurations,
        performModuleConfigurationAction,
        deleteModuleConfiguration,
        updateModuleConfiguration,
        getModuleConfiguration,
        downloadPentestReport,
        loadTemplates,
        loadRunners,
        loadAssets,
        loadNessusConfigs,
        createScan,
        loadDataSharingStatus,
        updateDataSharingData,
        updatePentestMailSettings,
      };
    },
  ),
);
